import { observable, makeObservable, computed } from 'mobx';
import { MedicationStatus } from 'chaika-enums';
import { Nullable } from 'chaika-common-types';
import { translate } from '@chaika/i18n/helper';
import { DateHelper } from '@utils/DateHelper';

export interface MedicationParams {
    id: string;
    activeSubstanceId: string;
    parentEntryId: string;
    authorId: string;
    tradeNameIds?: string[];
    customTradeNames?: string[];
    start?: string;
    end?: Nullable<string>;
    frequencyIfReception?: string;
    reasonForCancellation?: string;
    status: MedicationStatus;
    isDefault?: boolean;
    createdAt?: string;
    cancelledAt?: string;
    cancelledBy?: string;
}

export class Medications {
    @observable public id: string;
    @observable public activeSubstanceId: string;
    @observable public parentEntryId: string;
    @observable public authorId: string;
    @observable public tradeNameIds: string[];
    @observable public customTradeNames: string[];
    @observable public start?: string;
    @observable public end?: Nullable<string>;
    @observable public frequencyIfReception?: string;
    @observable public isDefault?: boolean;
    @observable public status: MedicationStatus;
    @observable public reasonForCancellation?: string;
    @observable public createdAt?: string;
    @observable public cancelledAt?: string;
    @observable public cancelledBy?: string;

    public constructor({
        id,
        activeSubstanceId = '',
        tradeNameIds = [],
        customTradeNames = [],
        start,
        end,
        frequencyIfReception,
        status,
        isDefault,
        parentEntryId,
        authorId,
        reasonForCancellation,
        createdAt,
        cancelledAt,
        cancelledBy,
    }: MedicationParams) {
        makeObservable(this);
        this.id = id;
        this.activeSubstanceId = activeSubstanceId;
        this.tradeNameIds = tradeNameIds;
        this.customTradeNames = customTradeNames;
        this.isDefault = isDefault;
        this.start = start;
        this.end = end;
        this.frequencyIfReception = frequencyIfReception;
        this.status = status;
        this.parentEntryId = parentEntryId;
        this.authorId = authorId;
        this.reasonForCancellation = reasonForCancellation;
        this.createdAt = createdAt;
        this.cancelledAt = cancelledAt;
        this.cancelledBy = cancelledBy;
    }

    @computed
    public get isActive(): boolean {
        return this.status === MedicationStatus.Active;
    }

    @computed
    public get isCancelled(): boolean {
        return (
            this.status === MedicationStatus.Deleted ||
            this.status === MedicationStatus.CancelledAppointment ||
            this.status === MedicationStatus.CanceledByPatient
        );
    }

    @computed
    public get endString(): string {
        return this.end
            ? `${translate('to')} ${DateHelper.formatDateWithYearAwareness(this.end)}`
            : translate('indefinitely');
    }

    @computed
    public get startEndString(): string {
        const startString = this.start
            ? `${translate('fromSecond')} ${DateHelper.formatDateWithYearAwareness(this.start)}`
            : undefined;
        const endString = `${this.end ? DateHelper.formatDateWithYearAwareness(this.end) : translate('indefinitely')}`;

        return [startString, endString].filter(Boolean).join(` ${this.end ? translate('to').toLowerCase() : ' '} `);
    }

    public serialize(): MedicationParams {
        return {
            id: this.id,
            activeSubstanceId: this.activeSubstanceId,
            tradeNameIds: this.tradeNameIds,
            customTradeNames: this.customTradeNames,
            start: this.start,
            isDefault: this.isDefault,
            end: this.end,
            frequencyIfReception: this.frequencyIfReception,
            status: this.status,
            parentEntryId: this.parentEntryId,
            reasonForCancellation: this.reasonForCancellation,
            authorId: this.authorId,
            createdAt: this.createdAt,
            cancelledAt: this.cancelledAt,
            cancelledBy: this.cancelledBy,
        };
    }
}
